const PLACEHOLDER_WORDS = [
  'producto',
  'product',
  'produto',
  'servicio',
  'service',
  'servico',
  'item',
  'articulo',
  'test',
  'prueba',
  'teste',
  'ejemplo',
  'example',
  'exemplo',
];

const PLACEHOLDER_NAME = new RegExp(
  `^(?:${PLACEHOLDER_WORDS.join('|')})(?:\\d+|[\\s#_-]+(?:\\d+|[a-z]))?$`,
);
const DIGITS_ONLY = /^\d+$/;
const SENTENCE_PUNCTUATION = /[.!?;]/;

export const MAX_SUGGESTED_NAME_WORDS = 6;
export const MAX_SUGGESTED_NAME_LENGTH = 60;
export const MAX_AGENT_PRODUCT_LINE_CHARS = 160;
export const MAX_PRODUCT_ALIASES = 20;
export const MAX_PRODUCT_ALIAS_LENGTH = 120;
export const MAX_PRODUCT_ALIASES_INPUT_LENGTH =
  MAX_PRODUCT_ALIASES * (MAX_PRODUCT_ALIAS_LENGTH + 1);

const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

const COMBINING_MARKS = /[\u0300-\u036f]/g;

const stripDiacritics = (value: string): string =>
  typeof value.normalize === 'function'
    ? value.normalize('NFD').replace(COMBINING_MARKS, '')
    : value;

export const normalizeProductName = (value: unknown): string =>
  typeof value === 'string' ? collapseWhitespace(stripDiacritics(value)).toLowerCase() : '';

export const isGenericProductName = (value: unknown): boolean => {
  const key = normalizeProductName(value);
  if (!key) return false;
  return DIGITS_ONLY.test(key) || PLACEHOLDER_NAME.test(key);
};

const looksLikeName = (value: string): boolean =>
  value.length <= MAX_SUGGESTED_NAME_LENGTH &&
  value.split(' ').length <= MAX_SUGGESTED_NAME_WORDS &&
  !SENTENCE_PUNCTUATION.test(value);

export const suggestNameFromDescription = (name: unknown, description: unknown): string | null => {
  if (!isGenericProductName(name) || typeof description !== 'string') return null;
  const candidate = collapseWhitespace(description);
  if (!candidate || !looksLikeName(candidate) || isGenericProductName(candidate)) return null;
  return candidate.charAt(0).toUpperCase() + candidate.slice(1);
};

export interface AgentProductFields {
  name: string;
  description?: string | null;
  priceRange?: string | null;
  aliases?: readonly string[] | null;
}

const ELLIPSIS = '…';

const truncateAgentLine = (line: string): string =>
  line.length <= MAX_AGENT_PRODUCT_LINE_CHARS
    ? line
    : `${line.slice(0, MAX_AGENT_PRODUCT_LINE_CHARS - 1)}${ELLIPSIS}`;

const headWithAliasesThatFit = (head: string, aliases: string[]): string => {
  for (let count = aliases.length; count > 0; count--) {
    const line = `${head} [alias: ${aliases.slice(0, count).join(', ')}]`;
    if (line.length <= MAX_AGENT_PRODUCT_LINE_CHARS) return line;
  }
  return head;
};

const appendDetails = (head: string, description: string, priceRange: string): string => {
  const price = priceRange ? ` (${priceRange})` : '';
  const whole = `${head}${description ? `: ${description}` : ''}${price}`;
  if (whole.length <= MAX_AGENT_PRODUCT_LINE_CHARS) return whole;

  const room =
    MAX_AGENT_PRODUCT_LINE_CHARS - head.length - price.length - ': '.length - ELLIPSIS.length;
  if (description && room > 0) {
    return `${head}: ${description.slice(0, room).trimEnd()}${ELLIPSIS}${price}`;
  }
  return truncateAgentLine(whole);
};

export const formatAgentProductLine = (product: AgentProductFields): string | null => {
  const name = collapseWhitespace(product.name);
  if (!name) return null;

  const description = collapseWhitespace(product.description ?? '');
  const priceRange = collapseWhitespace(product.priceRange ?? '');
  const aliases = (product.aliases ?? []).map(collapseWhitespace).filter(Boolean);

  return appendDetails(headWithAliasesThatFit(`- ${name}`, aliases), description, priceRange);
};

export type RejectedAliasReason = 'duplicate' | 'too_long' | 'capped';

export interface RejectedAlias {
  alias: string;
  reason: RejectedAliasReason;
}

export interface MergedProductAliases {
  aliases: string[];
  capped: boolean;
  rejected: RejectedAlias[];
}

export const dedupeAliases = (aliases: readonly string[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const raw of aliases) {
    const alias = collapseWhitespace(raw);
    const key = normalizeProductName(alias);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(alias);
  }
  return unique;
};

export const mergeProductAliases = (
  current: readonly string[],
  incoming: readonly string[],
  productName: string,
): MergedProductAliases => {
  const aliases = [...current];
  const seen = new Set([normalizeProductName(productName), ...current.map(normalizeProductName)]);
  const rejected: RejectedAlias[] = [];
  let capped = false;

  for (const raw of incoming) {
    const alias = collapseWhitespace(raw);
    const key = normalizeProductName(alias);
    if (!key) continue;
    if (seen.has(key)) {
      rejected.push({ alias, reason: 'duplicate' });
    } else if (alias.length > MAX_PRODUCT_ALIAS_LENGTH) {
      rejected.push({ alias, reason: 'too_long' });
    } else if (aliases.length >= MAX_PRODUCT_ALIASES) {
      capped = true;
      rejected.push({ alias, reason: 'capped' });
    } else {
      aliases.push(alias);
      seen.add(key);
    }
  }

  return { aliases, capped, rejected };
};
